import React, { useState, useRef, useEffect } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    ContentLayout,
    Box,
    Container,
    Header,
    Alert,
    StatusIndicator,
    Icon,
    Badge,
    TextContent,
    ColumnLayout,
    Modal,
    Spinner
} from '@amzn/awsui-components-console';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { getUserAtom, userDisplayNameAtom } from '../../contexts/userContext';
import { useProfile } from '../../hooks/useProfiles';
import { useErrorNavigation } from '../../hooks/useErrorNavigation';
import ChatInterfaceV2 from '../molecules/ChatInterfaceV2';
import ExcelPreview from '../molecules/ExcelPreview';
import './ProfileDetail.scss';

interface ProfileDetailProps {
    profileId?: string;
}

interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
    content?: any[][];
    lastModified: number;
    originalFile?: File;
}

const ProfileDetail: React.FC<ProfileDetailProps> = ({ profileId: propProfileId }) => {
    const { id: urlProfileId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { navigateToNotFound } = useErrorNavigation();
    
    // Get user info for avatar
    const authenticatedUser = useAtomValue(getUserAtom);
    const userDisplayName = useAtomValue(userDisplayNameAtom);
    
    // Use URL param if available, otherwise use prop
    const profileId = urlProfileId || propProfileId || '';
    
    // Fetch profile data using API
    const { profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile(profileId);
    
    // State management
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState<File | null>(null);

    
    // Use fetched profile data or fallback
    const profileData = profile ? {
        id: profile.id,
        name: profile.name,
        ldapGroup: profile.ldapGroup,
        description: profile.description || 'No description available',
        createdBy: profile.createdBy || 'Unknown',
        lastModified: profile.lastUpdated || new Date().toISOString(),
        status: profile.status || 'active'
    } : null;

    // Update URL if needed
    useEffect(() => {
        if (!urlProfileId && profileId) {
            // Update URL to include profile ID
            navigate(`/profile/${profileId}`, { replace: true });
        }
    }, [profileId, urlProfileId, navigate]);

    // Predefined questions for quick actions
    const predefinedQuestions = [
        "What are data columns to mapping to be used?",
        "Show me the expense breakdown",
        "Generate monthly financial report",
        "Analyze account reconciliation status",
        "What are the pending transactions?",
        "Show audit trail for last month"
    ];

    // Handle file upload
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        
        // Validate file type
        const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/)) {
            setUploadError('Please upload a CSV or Excel file');
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        
        // Store the actual File object for preview
        setPreviewFile(file);
        setShowPreview(true);

        // Create file object with original file reference
        const uploadedFile: UploadedFile = {
            id: `file-${Date.now()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            originalFile: file  // Store the original File object
        };

        // Add to uploaded files immediately
        setUploadedFiles(prev => [...prev, uploadedFile]);
        setIsUploading(false);
        
        // Clear the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle file removal
    const handleRemoveFile = (fileId: string) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    // Handle view jobs
    const handleViewJobs = () => {
        navigate(`/profile/${profileId}/jobs`);
    };

    // Redirect to error page if profile fails to load - MUST be before any conditional returns
    useEffect(() => {
        if (profileError || (profile && !profileData)) {
            navigateToNotFound('profile', profileId);
        }
    }, [profileError, profile, profileData, navigateToNotFound, profileId]);

    // Show loading state
    if (profileLoading || !profileData) {
        return (
            <div className="profile-detail-page">
                <ContentLayout
                    defaultPadding={true}
                    headerVariant="high-contrast"
                >
                    <Container>
                        <Box textAlign="center" padding={{ vertical: "xl" }}>
                            <Spinner size="large" />
                        </Box>
                    </Container>
                </ContentLayout>
            </div>
        );
    }

    return (
        <div className="profile-detail-page">
            <ContentLayout
                defaultPadding={true}
                headerVariant="high-contrast"
                header={
                    <Box padding={{ horizontal: "xxxl", vertical: "xxs" }}>
                        <div className="profile-header-content">
                            <SpaceBetween size="m" direction="vertical">
                                <BreadcrumbGroup
                                    items={[
                                        { text: "Home", href: "/" },
                                        { text: "My Profile", href: "/profiles" },
                                        { text: profileData.name, href: "#" }
                                    ]}
                                    ariaLabel="Breadcrumbs"
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                    <div style={{ flex: 1 }}>
                                        <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                            {profileData.name}
                                        </Box>
                                        <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                            {profileData.description}
                                        </Box>

                                        <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                            <Badge color="green">{profileData.ldapGroup || 'No LDAP Group'}</Badge>
                                        </Box>
                                       
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                                        <Button
                                            iconName="refresh"
                                            variant="normal"
                                            onClick={refreshProfile}
                                            ariaLabel="Refresh profile"
                                            loading={profileLoading}
                                        >
                                        
                                        </Button>
                                        <Button
                                            variant="primary"
                                            iconName="list-view"
                                            onClick={handleViewJobs}
                                        >
                                            View jobs
                                        </Button>
                                    </div>
                                </div>
                            </SpaceBetween>
                        </div>
                    </Box>
                }
            >
                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                />

                <Box>
                    {/* Upload error */}
                    {uploadError && (
                        <Box padding={{ horizontal: "xl", vertical: "l" }}>
                            <Alert
                                type="error"
                                dismissible
                                onDismiss={() => setUploadError(null)}
                                header="Upload failed"
                            >
                                {uploadError}
                            </Alert>
                        </Box>
                    )}

                        {/* Chat Interface V2 -- Agent-only */}
                        <ChatInterfaceV2
                            profileName={profileData.name}
                            profileId={profileId}
                            userName={userDisplayName || authenticatedUser?.username || 'User'}
                            userInitial={(userDisplayName || authenticatedUser?.username || 'U')[0].toUpperCase()}
                        />
            </Box>
                
                {/* Excel Preview Modal */}
                <Modal
                    visible={showPreview && previewFile !== null}
                    onDismiss={() => {
                        setShowPreview(false);
                        setPreviewFile(null);
                        // Clear the "Template preview opened" message from chat
                        if ((window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__) {
                            (window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__();
                        }
                    }}
                    size="max"
                    header="Document Preview"
                >
                    {previewFile && (
                        <ExcelPreview 
                            file={previewFile}
                            onClose={() => {
                                setShowPreview(false);
                                setPreviewFile(null);
                                // Clear the "Template preview opened" message from chat
                                if ((window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__) {
                                    (window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__();
                                }
                            }}
                        />
                    )}
                </Modal>
            </ContentLayout>
        </div>
    );
};

export default ProfileDetail;